<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onDestroy, untrack } from 'svelte';
    import settingsKeys from '../../../settings/settingsKeys.js';
    import ControlRow from './generic/ControlRow.svelte';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * @typedef {Object} Props
     * @property {Object} areaBroker
     * @property {Object} initialState
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        areaBroker,
        initialState,
        onChange
    } = $props();

    // svelte-ignore state_referenced_locally
    const targetElt = areaBroker?.getContainer();

    // stepper props
    const min = 0;
    const step = 1;
    const max = 2;
    let value = $state(0);

    const normalFontSizeValues = [1.5, 1.75, 2, 2, 2.5, 3.75, 5]; // default font-sizes (rem)
    const fontSizesMap = [
        {
            name: 'normal',
            values: normalFontSizeValues
        },
        {
            name: 'large',
            values: normalFontSizeValues.map(size => size * 1.25)
        },
        {
            name: 'extraLarge',
            values: normalFontSizeValues.map(size => size * 1.5)
        }
    ];
    const defaultValue = 0;

    const initialStateOnce = untrack(() => initialState);
    if (initialStateOnce?.value) {
        handleFontSizeChange(initialStateOnce);
    }
    let nonDefault = initialStateOnce?.nonDefault;

    /**
     * Handle the input being changed
     * @param {Object} [newState]
     */
    function handleFontSizeChange(newState = {}) {
        value = newState.value;
        const { name, values } = fontSizesMap[value] || fontSizesMap[defaultValue];

        if (targetElt) {
            const fontSizeScale = values[2] / fontSizesMap[defaultValue].values[2];

            if (value !== defaultValue) {
                targetElt.dataset.a11yOverrideFontSize = 'true';
                targetElt.style.setProperty('--a11y-fontsize-scale', String(fontSizeScale));
            } else {
                delete targetElt.dataset.a11yOverrideFontSize;
                targetElt.style.removeProperty('--a11y-fontsize-scale');
            }

            targetElt.style.setProperty('--fontsize-body-xs', `${values[0]}rem`);
            targetElt.style.setProperty('--fontsize-body-s', `${values[1]}rem`);
            targetElt.style.setProperty('--fontsize-body', `${values[2]}rem`);
            targetElt.style.setProperty('--fontsize-heading', `${values[3]}rem`);
            targetElt.style.setProperty('--fontsize-heading-l', `${values[4]}rem`);
            targetElt.style.setProperty('--fontsize-heading-xl', `${values[5]}rem`);
            targetElt.style.setProperty('--fontsize-heading-xxl', `${values[6]}rem`);
        }

        onChange?.({
            key: settingsKeys.fontSize,
            state: {
                value,
                fontSizeBody: `${values[2]}rem`,
                fontSizeDescriptor: name,
                nonDefault: newState.nonDefault
            }
        });
    }

    onDestroy(() => {
        delete targetElt?.dataset.a11yOverrideFontSize;

        targetElt?.style.removeProperty('--fontsize-body-xs');
        targetElt?.style.removeProperty('--fontsize-body-s');
        targetElt?.style.removeProperty('--fontsize-body');
        targetElt?.style.removeProperty('--fontsize-heading');
        targetElt?.style.removeProperty('--fontsize-heading-l');
        targetElt?.style.removeProperty('--fontsize-heading-xl');
        targetElt?.style.removeProperty('--fontsize-heading-xxl');

        targetElt?.style.removeProperty('--a11y-fontsize-scale');
    });
</script>

<ControlRow
    icon="font-size-16"
    label={__('Size')}
    {min}
    {max}
    {step}
    {value}
    {nonDefault}
    ariaLabelDecr={__('decrease font size')}
    ariaLabelIncr={__('increase font size')}
    onChange={handleFontSizeChange} />
